import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Moon, Sun, Home } from 'lucide-react';
import { daysOfCoverService } from '../../services/daysOfCoverService';
import type { DaysOfCoverCompany, DaysOfCoverResponse } from '../../types/daysOfCover';
import CoverageDistributionChart from './CoverageDistributionChart';
import AlertPanels from './AlertPanels';
import DataTable from './DataTable';
import { useAuth } from '../../context/AuthContext';
import { useDarkMode } from '../../context/DarkModeContext';
import { UserAvatar } from '../ui/user-avatar';
import { employeeNamesService } from '../../services/employeeNames';
import { NotificationMenu } from '../notifications/NotificationMenu';

const DaysOfCoverDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { userEmail, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  
  const [companies, setCompanies] = useState<DaysOfCoverCompany[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [daysOfCoverData, setDaysOfCoverData] = useState<DaysOfCoverResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string>('');
  const [lastSyncDate, setLastSyncDate] = useState<Date | null>(null);
  const [syncStatus, setSyncStatus] = useState<'success' | 'error' | 'loading'>('loading');

  // Load display name
  useEffect(() => {
    const loadDisplayName = async () => {
      if (userEmail) {
        const name = await employeeNamesService.getNameFromEmail(userEmail);
        if (name) {
          setDisplayName(name);
        }
      }
    };
    loadDisplayName();
  }, [userEmail]);

  // Load companies on component mount
  useEffect(() => {
    let isMounted = true;
    
    const loadCompanies = async () => {
      try {
        setLoading(true);
        const companiesData = await daysOfCoverService.getCompanies();
        
        if (isMounted) {
          setCompanies(companiesData);
          // Auto-select first company
          if (companiesData.length > 0 && !selectedCompany) {
            setSelectedCompany(companiesData[0].id);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError('Failed to load companies. Please refresh the page.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Only load if we don't have companies yet
    if (companies.length === 0) {
      loadCompanies();
    }

    return () => {
      isMounted = false;
    };
  }, [companies.length, selectedCompany]);

  // Load Days of Cover data when company is selected
  useEffect(() => {
    if (selectedCompany) {
      const loadDaysOfCoverData = async () => {
        try {
          setLoading(true);
          setError(null);
          const data = await daysOfCoverService.getDaysOfCoverData(selectedCompany);
          setDaysOfCoverData(data);
        } catch (err) {
          setError('Failed to load Days of Cover data. Please try again.');
        } finally {
          setLoading(false);
        }
      };

      loadDaysOfCoverData();
    }
  }, [selectedCompany]);

  // Subscribe to sync status for the selected company
  useEffect(() => {
    if (!selectedCompany) {
      setLastSyncDate(null);
      setSyncStatus('loading');
      return;
    }

    const unsubscribe = daysOfCoverService.subscribeSyncStatus(selectedCompany, (status) => {
      if (status) {
        // Check for successful completion using actual Firebase field names
        if (status.status === 'completed' && status.completedAt) {
          // Use completedAt timestamp from Firebase
          const syncDate = status.completedAt.toDate ? status.completedAt.toDate() : new Date(status.completedAt);
          setLastSyncDate(syncDate);
          setSyncStatus('success');
        } else if (status.error || status.status === 'failed') {
          setSyncStatus('error');
        } else if (status.status === 'running') {
          setSyncStatus('loading');
        } else {
          // Default case for unknown status
          setSyncStatus('error');
        }
      } else {
        // No sync status document yet
        setSyncStatus('error');
      }
    });

    return () => unsubscribe();
  }, [selectedCompany]);

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '--';
    return value.toLocaleString();
  };

  const formatPercentage = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '--%';
    return `${value.toFixed(1)}%`;
  };

  // Helper functions to get actual alert data with items
  const getCriticalItems = (data: DaysOfCoverResponse | null) => {
    if (!data || !data.rows.length) return [];
    return data.rows.filter(row => {
      const daysOfCover = parseFloat(row[6]) || 0;
      const active = (row[7] || '').toLowerCase() === 'yes';
      return daysOfCover < 15 && active;
    }).map(row => ({
      asin: row[0] || '',
      productName: row[1] || '',
      daysOfCover: parseFloat(row[6]) || 0
    })).sort((a, b) => a.daysOfCover - b.daysOfCover); // Sort low to high (most critical first)
  };

  const getOverstockItems = (data: DaysOfCoverResponse | null) => {
    if (!data || !data.rows.length) return [];
    return data.rows.filter(row => {
      const daysOfCover = parseFloat(row[6]) || 0;
      const active = (row[7] || '').toLowerCase() === 'yes';
      return daysOfCover > 45 && active;
    }).map(row => ({
      asin: row[0] || '',
      productName: row[1] || '',
      daysOfCover: parseFloat(row[6]) || 0
    })).sort((a, b) => b.daysOfCover - a.daysOfCover); // Sort high to low (highest overstock first)
  };

  const getInactiveItems = (data: DaysOfCoverResponse | null) => {
    if (!data || !data.rows.length) return [];
    return data.rows.filter(row => {
      const daysOfCover = parseFloat(row[6]) || 0;
      const active = (row[7] || '').toLowerCase() === 'yes';
      return !active && daysOfCover > 0;
    }).map(row => ({
      asin: row[0] || '',
      productName: row[1] || '',
      daysOfCover: parseFloat(row[6]) || 0
    }));
  };

  // Helper functions to calculate coverage distribution
  const getCoverageDistribution = (data: DaysOfCoverResponse | null) => {
    if (!data || !data.rows.length) {
      return {
        critical: 0,
        low: 0,
        medium: 0,
        overstock: 0,
        total: 0
      };
    }

    const activeRows = data.rows.filter(row => (row[7] || '').toLowerCase() === 'yes');
    const total = activeRows.length;
    
    let critical = 0, low = 0, medium = 0, overstock = 0;
    
    activeRows.forEach(row => {
      const daysOfCover = parseFloat(row[6]) || 0;
      if (daysOfCover < 15) critical++;
      else if (daysOfCover < 30) low++;
      else if (daysOfCover < 45) medium++;
      else overstock++;
    });

    return {
      critical: total > 0 ? (critical / total) * 100 : 0,
      low: total > 0 ? (low / total) * 100 : 0,
      medium: total > 0 ? (medium / total) * 100 : 0,
      overstock: total > 0 ? (overstock / total) * 100 : 0,
      total
    };
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 z-40 border-b dark:border-gray-700 px-4 flex items-center">
        <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
          <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <h1 className="ml-4 text-lg font-semibold text-gray-900 dark:text-white">Days of Cover</h1>
      </div>

      {/* Sidebar */}
      <aside className="bg-white dark:bg-gray-800 shadow-sm w-64 md:w-64 flex flex-col hidden md:flex">
        {/* Header Section with Days of Cover Branding */}
        <div className="border-b flex justify-center items-center h-23 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
          <div className="flex flex-col items-center justify-center py-4">
            <svg className="h-10 w-10 text-emerald-600 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">Days of Cover</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Inventory Analysis</p>
          </div>
        </div>
        
        {/* Navigation Content */}
        <nav className="flex-1 mt-4 overflow-y-auto flex flex-col">
          <div className="px-4 py-2 mb-4 space-y-2">
            {/* Manage Companies Section */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">
                Manage Companies
              </label>
              <select
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:focus:ring-emerald-400"
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                disabled={loading}
              >
                <option value="">
                  {loading ? 'Loading companies...' : companies.length === 0 ? 'No companies available' : 'Select a company'}
                </option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.client}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {companies.length > 0 ? `${companies.length} companies available` : 'Select a company to view Days of Cover data'}
              </p>
            </div>

            {/* Navigation Buttons */}
            <div className="mt-6 space-y-2">
              {/* Dashboard Button - Active State */}
              <button
                className="w-full flex items-center px-3 py-2 text-sm bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 rounded-md border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                tabIndex={0}
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Dashboard
                <span className="ml-auto text-xs bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-300 px-2 py-1 rounded-full">Active</span>
              </button>

            </div>

            {/* Divider */}
            <div className="mx-4 my-4 border-t border-gray-200 dark:border-gray-700" />

            {/* Apps Hub Link */}
            <div
              onClick={() => navigate('/dashboard')}
              className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
            >
              <Home className="mr-3 h-5 w-5" />
              Apps Hub
            </div>
          </div>

          {/* Spacer to push footer to bottom */}
          <div className="flex-1" />
        </nav>

        {/* Footer with User Info */}
        <div className="border-t dark:border-gray-700 p-4 mt-auto bg-white dark:bg-gray-800">
          <div className="flex flex-col gap-4">
            {displayName && (
              <div className="text-center">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {displayName}
                </span>
              </div>
            )}
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => {
                  logout();
                  navigate('/');
                }}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
              <button
                onClick={toggleDarkMode}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                title={isDarkMode ? "Light Mode" : "Dark Mode"}
              >
                {isDarkMode ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
              </button>
              <NotificationMenu>
                <UserAvatar size="sm" className="h-8 w-8" />
              </NotificationMenu>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto pt-16 md:pt-0 relative">
        <div className="p-4 md:p-6">
          {/* Last Updated Indicator - Top Left */}
          <div className="flex justify-start mb-6">
            <div className="flex items-center text-sm">
              {syncStatus === 'success' && lastSyncDate && (
                <div className="flex items-center text-green-600 dark:text-green-400">
                  <span className="mr-1">✅</span>
                  <span>Last Successful Sync: {lastSyncDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                </div>
              )}
              {syncStatus === 'error' && (
                <div className="flex items-center text-red-600 dark:text-red-400">
                  <span className="mr-1">❌</span>
                  <span>Last sync failed</span>
                  {lastSyncDate && (
                    <span className="ml-1">(last success: {lastSyncDate.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })})</span>
                  )}
                </div>
              )}
              {syncStatus === 'loading' && (
                <div className="flex items-center text-blue-600 dark:text-blue-400">
                  <span className="mr-1">🔄</span>
                  <span>Syncing...</span>
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Development Mode</h3>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="animate-spin h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-sm text-blue-700 dark:text-blue-300">Loading data...</p>
              </div>
            </div>
          )}

          {/* Two Row Card Layout */}
          {/* First Row - KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-4">
            {/* Average Days of Cover */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md hover:border-emerald-300 dark:hover:border-emerald-600 transition-all duration-200 cursor-pointer group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">Average DOC</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                    {formatNumber(daysOfCoverData?.kpis.averageDaysOfCover)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">days</p>
                </div>
                <div className="p-3 bg-emerald-100 dark:bg-emerald-900/20 rounded-full group-hover:bg-emerald-200 dark:group-hover:bg-emerald-900/40 transition-colors">
                  <svg className="h-6 w-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Low Coverage Percentage */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md hover:border-red-300 dark:hover:border-red-600 transition-all duration-200 cursor-pointer group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">Low Coverage</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                    {formatPercentage(daysOfCoverData?.kpis.lowCoveragePercent)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">&lt; 7 days</p>
                </div>
                <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-full group-hover:bg-red-200 dark:group-hover:bg-red-900/40 transition-colors">
                  <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Total Inventory */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200 cursor-pointer group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">Total Inventory</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                    {formatNumber(daysOfCoverData?.kpis.totalInventory)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">units</p>
                </div>
                <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-full group-hover:bg-blue-200 dark:group-hover:bg-blue-900/40 transition-colors">
                  <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Active SKUs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-200 cursor-pointer group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">Active SKUs</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                    {formatNumber(daysOfCoverData?.rows.length)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">products</p>
                </div>
                <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-full group-hover:bg-purple-200 dark:group-hover:bg-purple-900/40 transition-colors">
                  <svg className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Second Row - Redesigned Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
            {/* Cover Distribution - Redesigned */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              {(() => {
                const distribution = getCoverageDistribution(daysOfCoverData);
                return (
                  <div className="space-y-3">
                    {/* Title with Icon and Count */}
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <h4 className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                        Cover Distribution ({distribution.total} items)
                      </h4>
                    </div>

                    {/* Percentages Display */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <div className="flex items-center justify-between px-1">
                        <span className="text-red-600 dark:text-red-400">Critical:</span>
                        <span className="font-medium text-red-600 dark:text-red-400 ml-3">{distribution.critical.toFixed(1)}%</span>
                      </div>
                      <div className="flex items-center justify-between px-1">
                        <span className="text-orange-600 dark:text-orange-400">Low:</span>
                        <span className="font-medium text-orange-600 dark:text-orange-400 ml-3">{distribution.low.toFixed(1)}%</span>
                      </div>
                      <div className="flex items-center justify-between px-1">
                        <span className="text-yellow-600 dark:text-yellow-400">Medium:</span>
                        <span className="font-medium text-yellow-600 dark:text-yellow-400 ml-3">{distribution.medium.toFixed(1)}%</span>
                      </div>
                      <div className="flex items-center justify-between px-1">
                        <span className="text-green-600 dark:text-green-400">Overstock:</span>
                        <span className="font-medium text-green-600 dark:text-green-400 ml-3">{distribution.overstock.toFixed(1)}%</span>
                      </div>
                    </div>

                    {/* Horizontal Bar - Clean without percentages */}
                    <div className="relative h-8 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="absolute left-0 top-0 h-full bg-red-500"
                        style={{ width: `${distribution.critical}%` }}
                      />
                      <div
                        className="absolute top-0 h-full bg-orange-500"
                        style={{
                          left: `${distribution.critical}%`,
                          width: `${distribution.low}%`
                        }}
                      />
                      <div
                        className="absolute top-0 h-full bg-yellow-500"
                        style={{
                          left: `${distribution.critical + distribution.low}%`,
                          width: `${distribution.medium}%`
                        }}
                      />
                      <div
                        className="absolute top-0 h-full bg-green-600"
                        style={{
                          left: `${distribution.critical + distribution.low + distribution.medium}%`,
                          width: `${distribution.overstock}%`
                        }}
                      />
                    </div>

                    {/* Legend */}
                    <div className="text-xs space-y-1">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-red-500 rounded mr-2"></div>
                        <span className="text-gray-600 dark:text-gray-400">Critical: less than 15 days</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-orange-500 rounded mr-2"></div>
                        <span className="text-gray-600 dark:text-gray-400">Low: 15–30 days</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-yellow-500 rounded mr-2"></div>
                        <span className="text-gray-600 dark:text-gray-400">Medium: 30–45 days</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-green-600 rounded mr-2"></div>
                        <span className="text-gray-600 dark:text-gray-400">Overstock: 45+ days</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Critical Stock Alert Card with Data - Under Low Coverage */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              {(() => {
                const criticalItems = getCriticalItems(daysOfCoverData);
                return (
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                        Critical Stock ({criticalItems.length} items)
                      </h4>
                    </div>
                    <div className="space-y-2">
                      {criticalItems.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No items in this category</p>
                      ) : (
                        <>
                          {criticalItems.slice(0, 6).map((item, index) => (
                            <div key={index} className="flex justify-between items-center text-sm gap-2">
                              <a
                                href={`https://www.amazon.com/dp/${item.asin}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-red-700 dark:text-red-300 font-medium truncate hover:underline flex-1 min-w-0"
                              >
                                {item.productName}
                              </a>
                              <span className="text-red-600 dark:text-red-400 flex-shrink-0">{Math.round(item.daysOfCover)} days</span>
                            </div>
                          ))}
                          {criticalItems.length > 6 && (
                            <p className="text-xs text-red-600 dark:text-red-400">
                              +{criticalItems.length - 6} more items
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Overstock Alert Card with Data - Under Total Inventory */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              {(() => {
                const overstockItems = getOverstockItems(daysOfCoverData);
                return (
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                        Overstock ({overstockItems.length} items)
                      </h4>
                    </div>
                    <div className="space-y-2">
                      {overstockItems.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No items in this category</p>
                      ) : (
                        <>
                          {overstockItems.slice(0, 6).map((item, index) => (
                            <div key={index} className="flex justify-between items-center text-sm gap-2">
                              <a
                                href={`https://www.amazon.com/dp/${item.asin}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-700 dark:text-blue-300 font-medium truncate hover:underline flex-1 min-w-0"
                              >
                                {item.productName}
                              </a>
                              <span className="text-blue-600 dark:text-blue-400 flex-shrink-0">{Math.round(item.daysOfCover)} days</span>
                            </div>
                          ))}
                          {overstockItems.length > 6 && (
                            <p className="text-xs text-blue-600 dark:text-blue-400">
                              +{overstockItems.length - 6} more items
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Inactive Products Alert Card with Data */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              {(() => {
                const inactiveItems = getInactiveItems(daysOfCoverData);
                return (
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-gray-600 dark:text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                      <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        Inactive Products ({inactiveItems.length} items)
                      </h4>
                    </div>
                    <div className="space-y-2">
                      {inactiveItems.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No items in this category</p>
                      ) : (
                        <>
                          {inactiveItems.slice(0, 6).map((item, index) => (
                            <div key={index} className="flex justify-between items-center text-sm gap-2">
                              <a
                                href={`https://www.amazon.com/dp/${item.asin}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-700 dark:text-gray-300 font-medium truncate hover:underline flex-1 min-w-0"
                              >
                                {item.productName}
                              </a>
                              <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">{Math.round(item.daysOfCover)} days</span>
                            </div>
                          ))}
                          {inactiveItems.length > 6 && (
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              +{inactiveItems.length - 6} more items
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Inventory Data Table - Full Width */}
          <div className="mb-6">
            <DataTable data={daysOfCoverData} />
          </div>

        </div>
      </main>
    </div>
  );
};

export default DaysOfCoverDashboard;